import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { PasswordInput } from '../../features/auth/components/ui/PasswordInput';
import { Input } from '../../shared/components';
import Button from '../../shared/components/Button';
import { WatchmanFetch } from '../../shared/utils/watchmanFetch';
import { useAuthStore } from '../../features/auth/store';
import type { ClaimInvitePayload } from '../../features/merchants/types';

const rules = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p: string) => /\d/.test(p) },
  { label: 'One special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useAuthStore();
  const [form, setForm] = useState({ password: '', confirm: '', fullName: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = rules.filter(r => r.test(form.password)).length;

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500'][strength];

  // Password-reset flow (forgot-password link) vs first-login merchant claim.
  const isReset = searchParams.get('type') === 'reset';
  const token = searchParams.get('token');
  // The merchant's own profile-completion fields (PE-2250) only apply to a genuine invite
  // claim — a forgot-password reset or an already-authenticated initial password change
  // don't touch fullName/phone.
  const isClaim = !isReset && !!token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (strength < 4) { setError('Password does not meet all requirements'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (isClaim && !form.fullName.trim()) { setError('Full name is required'); return; }

    setLoading(true);

    if (isReset) {
      // Forgot-password reset claim. A failure here MUST surface to the user —
      // never silently fall through to onboarding.
      try {
        await WatchmanFetch.post('/auth/reset-password-claim', { token, password: form.password });
        navigate('/login?reset=1');
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Could not reset your password. The link may have expired — please request a new one.';
        setError(message);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      if (token) {
        const claimPayload: ClaimInvitePayload = {
          token,
          password: form.password,
          fullName: form.fullName.trim(),
          ...(form.phone.trim() ? { phoneNumber: form.phone.trim() } : {}),
        };
        await WatchmanFetch.post('/merchants/onboard/claim', claimPayload);
        navigate('/login?claimed=1');
      } else {
        await WatchmanFetch.patch('/auth/reset-password', { newPassword: form.password });
        if (user) setUser({ ...user, isProfileCompleted: true });
        navigate('/');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Could not set your password. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout currentStep={1}>
      <div className="bg-card text-card-foreground border border-border rounded-[20px] p-8 shadow-sm">
        <div className="mb-6">
          <h1 className="text-[22px] font-[700] text-foreground">
            {isReset ? 'Reset your password' : 'Set your password'}
          </h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            {isReset
              ? 'Choose a new password for your account.'
              : "You're logging in for the first time. Please set a secure password before continuing."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* PE-2250 — the merchant administrator's own full name and phone number are
              collected HERE (their own profile-completion step) rather than by the
              collective admin at merchant-creation time. */}
          {isClaim && (
            <>
              <Input
                name="fullName"
                label="Full Name"
                placeholder="e.g. Jane Doe"
                value={form.fullName}
                onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                required
              />
              <Input
                name="phone"
                label="Phone Number"
                placeholder="e.g. +44 7700 900000 (optional)"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              />
            </>
          )}
          <div>
            <PasswordInput
              name="password"
              label="New Password"
              placeholder="Enter a strong password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
            />
            {/* Strength bar */}
            {form.password && (
              <div className="mt-2 space-y-1.5">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= strength ? strengthColor : 'bg-muted'}`} />
                  ))}
                </div>
                <span className={`text-[12px] font-[600] ${['', 'text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-600'][strength]}`}>
                  {strengthLabel}
                </span>
                <ul className="space-y-1 mt-1">
                  {rules.map(rule => (
                    <li key={rule.label} className={`flex items-center gap-1.5 text-[12px] ${rule.test(form.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        {rule.test(form.password)
                          ? <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          : <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        }
                      </svg>
                      {rule.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <PasswordInput
            name="confirm"
            label="Confirm Password"
            placeholder="Re-enter your password"
            value={form.confirm}
            onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
            error={form.confirm && form.password !== form.confirm ? 'Passwords do not match' : undefined}
            required
          />

          {error && <p className="text-destructive text-[13px]">{error}</p>}

          <Button type="submit" loading={loading} className="w-full mt-2">
            {isReset ? 'Reset Password' : 'Set Password & Continue'}
          </Button>
        </form>
      </div>
    </OnboardingLayout>
  );
};
